<template>
  <TheDashboard :user="user" :breadcrumbs="breadcrumbs">
    <template #logout-btn>
      <ClientLogoutBtn />
    </template>
    <template v-if="user" #sidebar="{ vOnNavigate, vOnClose, ...attrs }">
      <DashboardSidebar
        v-bind="attrs"
        :features="sidebarFeatures"
        :is-admin="isAdmin"
        @navigate="vOnNavigate"
        @close="vOnClose"
      >
        <template #default="{ selectedCallback }">
          <DashboardSidebarContentSection
            v-if="showDepartmentToggle"
            v-show="selectedFeature?.hasDepartments"
          >
            <DashboardSidebarSection title="Department">
              <VRadioControl
                :model-value="saleType"
                name="sale-type"
                :options="saleTypeOptions"
                radio-class="sidebar"
                @update:model-value="(value: string | number | boolean) => onSaleTypeSelected(value as ESaleType, selectedCallback)"
              />
            </DashboardSidebarSection>
          </DashboardSidebarContentSection>
          <slot name="sidebar" :selected-callback="selectedCallback" />
        </template>
      </DashboardSidebar>
    </template>
    <VWrapper>
      <slot />
    </VWrapper>
  </TheDashboard>
</template>

<script lang="ts" setup>
  // Types
  import type { RouteLocationNormalizedLoaded } from 'vue-router';
  import type { TAuthStore } from '~/modules/auth/store/types';
  import type { TUser, TPatch } from '~/misc/auth/types';
  import type { TListingSaleTypeOption } from '~/misc/listings/types';
  import type { TListingsStore } from '~/misc/listings/store/types';
  import type { TFeature } from '~/modules/dashboard/types';
  import { ERoute } from '~/modules/dashboard/types';
  import { EFeatureName } from '~/misc/feature/types';
  import { EAnalyticsEvent } from '~/misc/services/analytics/types';
  import { ESaleType } from '~/misc/listings/types';

  // Config
  import { FEATURES, FEATURES_OBJECT } from '~/modules/dashboard/config';

  // Utils
  import { eventBus } from '~/misc/utils/event-bus';

  // Store
  import { useAuthStore } from '~/modules/auth/store';
  import { useListingsStore } from '~/misc/listings/store';

  // Services
  import { AuthAccess } from '~/misc/auth/services/access';

  // Components
  import TheDashboard from '~/modules/dashboard/TheDashboard.vue';
  import ClientLogoutBtn from '~/modules/auth/client-logout-btn/ClientLogoutBtn.vue';
  import DashboardSidebar from '~/modules/dashboard/sidebar/DashboardSidebar.vue';
  import VWrapper from '~/misc/v-wrapper/VWrapper.vue';
  import DashboardSidebarContentSection from '~/modules/dashboard/sidebar/content-section/DashboardSidebarContentSection.vue';
  import VRadioControl from '~/misc/v-radio-control/VRadioControl.vue';
  import DashboardSidebarSection from '~/modules/dashboard/sidebar/section/DashboardSidebarSection.vue';

  const props = defineProps({
    extraBreadcrumbs: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  });

  const route: RouteLocationNormalizedLoaded = useRoute();
  const authStore: TAuthStore = useAuthStore();
  const listingsStore: TListingsStore = useListingsStore();

  const user: ComputedRef<TUser | null> = computed<TUser | null>(() => authStore.user);

  const userFeatures: ComputedRef<EFeatureName[]> = computed<EFeatureName[]>(
    () => authStore.features
  );

  const sidebarFeatures: ComputedRef<TFeature[]> = computed<TFeature[]>(() =>
    FEATURES.filter(
      (feature: TFeature) =>
        !feature.privateName || userFeatures.value.includes(feature.privateName)
    )
  );

  const selectedFeature: ComputedRef<TFeature | undefined> = computed<TFeature | undefined>(
    () => FEATURES_OBJECT[route.name as ERoute] ?? undefined
  );

  const saleType: ComputedRef<ESaleType> = computed<ESaleType>(() => listingsStore.filters.type);

  const hasSalesAreas: ComputedRef<boolean> = computed<boolean>(() =>
    authStore.patches.some((p: TPatch) => p.type === 'sale' || p.type === 'both')
  );

  const hasLettingsAreas: ComputedRef<boolean> = computed<boolean>(() =>
    authStore.patches.some((p: TPatch) => p.type === 'let' || p.type === 'both')
  );

  const saleTypeOptions: ComputedRef<TListingSaleTypeOption[]> = computed<TListingSaleTypeOption[]>(
    () => {
      const options: TListingSaleTypeOption[] = [];

      if (hasSalesAreas.value) {
        options.push({ label: 'Sales', value: ESaleType.SALES });
      }

      if (hasLettingsAreas.value) {
        options.push({ label: 'Lettings', value: ESaleType.LETTINGS });
      }

      return options;
    }
  );

  const showDepartmentToggle: ComputedRef<boolean> = computed<boolean>(
    () => saleTypeOptions.value.length > 0
  );

  const selectedFeatureLabel: ComputedRef<string> = computed<string>(
    () => selectedFeature.value?.title ?? '-'
  );

  const selectedSaleTypeLabel: ComputedRef<string> = computed<string>(
    () =>
      saleTypeOptions.value.find((item: TListingSaleTypeOption) => item.value === saleType.value)
        ?.label ?? ''
  );

  const breadcrumbs: ComputedRef<string[]> = computed<string[]>(() =>
    [
      selectedFeatureLabel.value,
      selectedFeature.value?.hasDepartments ? selectedSaleTypeLabel.value : '',
      ...props.extraBreadcrumbs,
    ].filter((item: string) => !!item)
  );

  const isAdmin: ComputedRef<boolean> = computed<boolean>(
    () => !!user.value && AuthAccess.canAdministrate(user.value)
  );

  const onSaleTypeSelected = (type: ESaleType | undefined, callback: Function) => {
    if (type) {
      eventBus.emit(EAnalyticsEvent.DASHBOARD_SIDEBAR_DEPARTMENT_CHANGED, {
        value: type,
      });
    }

    setSaleType(type);
    callback();
  };

  const setSaleType = (type: ESaleType | undefined) => {
    listingsStore.applyFilters({ type });
  };

  // Auto-select appropriate department based on available areas
  watch(
    () => authStore.patches,
    (patches: TPatch[]) => {
      if (!patches.length) return;

      // If current selection is valid, keep it
      if (saleType.value === ESaleType.SALES && hasSalesAreas.value) return;
      if (saleType.value === ESaleType.LETTINGS && hasLettingsAreas.value) return;

      // Auto-select based on availability (prefer sales)
      if (hasSalesAreas.value) {
        setSaleType(ESaleType.SALES);
      } else if (hasLettingsAreas.value) {
        setSaleType(ESaleType.LETTINGS);
      }
    },
    { immediate: true }
  );
</script>
